// src/context/ProtectedRoute.tsx
import { useContext } from "react";
import { Navigate } from "react-router-dom";
import ModulesContext from "./ModulesContext";

/** Names a permission used to have; a database that has not run the rename still sends them. */
const PERMISSION_ALIASES: Record<string, string[]> = {
  "Table Reservations": ["Reservations"],
};

export const COMPONENT_TO_PERMISSION_MAP: Record<string, string> = {
  Dashboard: "Dashboard",
  Promotion: "Promotion",
  CreatePromotions: "Promotion",
  Company: "Company Details",
  UserCompany: "Company Details",
  LocationCompany: "Company Details",
  Locations: "Locations",
  CreateLocations: "Locations",
  ListFloorPlan: "Floor Plans",
  FloorPlan: "Floor Plans",
  SalePoint: "Sale Points",
  SalePointDetail: "Sale Points",
  Reservations: "Reservation Setups",
  // Bookings list + detail. The permission is "Table Reservations" since backend migration
  // 1778665000000; PERMISSION_ALIASES below still lets the old "Reservations" name through.
  TableReservations: "Table Reservations",
  TableReservationsDetail: "Table Reservations",
  TableReservationsCreate: "Table Reservations",
  // Take-away customer orders (dbo.modules row seeded by 1784200000000).
  CustomerOrders: "Customer Orders",
  CustomerOrderDetail: "Customer Orders",
  CustomerOrderCreate: "Customer Orders",
  Employees: "Employees",
  CreateEmployees: "Employees",
  Clients: "Clients",
  PageCreateClients: "Clients",
  Items: "Items",
  CreateItems: "Items",
  CreateGroups: "Items",
  // FIN-1125: bulk product import pages reuse the existing "Items" permission.
  // Anyone with Items access can preview / confirm / view import history.
  ProductImport: "Items",
  ProductImportHistory: "Items",
  ProductImportBatchDetail: "Items",
  Attributes: "Attributes",
  CreateAttribuites: "Attributes",
  ProductModifiers: "Product Modifiers",
  CreateProductModifier: "Product Modifiers",
  // finex-web's Inventory page: builds before its list-kit rename call it SalesLedger$1.
  SalesLedger$1: "Inventory",
  Stocks: "Inventory",
  Order: "Order Management",
  OrderDetail: "Order Management",
  Transfer: "Transfers",
  TransferDetail: "Transfers",
  Deliver: "Order Management",
  DeliveriesDetails: "Order Management",
  Count: "Stock Counts",
  CountDetail: "Stock Counts",
  CountApproval: "Stock Counts",
  CountApprovalDetail: "Stock Counts",
  InventoryCorrections: "Corrections",
  CreateInventoryCorrections: "Corrections",
  OrderTemplates: "Order Templates",
  OrderTemplatesDetail: "Order Templates",
  OrderApproval: "Order Management",
  OrderApprovalDetail: "Order Management",
  SupplierInventory: "Supplier Inventory",
  SupplierInventoryDetail: "Supplier Inventory",
  Devices: "Devices",
  DeviceDetail: "Devices",
  DeviceUser: "Device Users",
  DeviceUserDetail: "Device Users",
  DeviceTender: "Device Tender",
  DeviceTenderDetail: "Device Tender",
  DeviceHealth: "Device Health",
  AppVersion: "App Version",
  CreateAppVersion: "App Version",
  Reasons: "Reasons",
  ReasonDetail: "Reasons",
  Schedules: "Schedules",
  SchedulesDetails: "Schedules",
  Tax: "Tax",
  CreateTax: "Tax",
  CreateGroupTax: "Tax",
  Terms: "Payment Terms",
  TermDetail: "Payment Terms",
  ListRole: "Roles",
  DeviceUserRole: "Roles",
  DeviceUserRoleDetail: "Roles",
  PageRole: "Roles",
  Languages: "Languages",
  CreateLanguage: "Languages",
  CreateTranslation: "Languages",
  Purchases: "Purchases",
  PagePurchase: "Purchases",
  Sales: "Sales",
  PageSales: "Sales",
  Assets: "Assets",
  PageAssets: "Assets",
  PurchaseLedger: "Purchase Ledger",
  SalesLedger: "Sales Ledger",
  TransactionMonth: "Transaction Month",
  GeneralReport: "General Report",
  ItemMovementReport: "Item Movement",
  RecentSales: "Recent Sales",
  RecentSalesDetail: "Recent Sales",
  SalesOverview: "Recent Sales",
  ProductPerformance: "Product Performance",
  FinancialReports: "Financial Reports",
  SupplierReturn: "Supplier Return",
  SupplierReturnDetails: "Supplier Return",
  JournalEntries: "Journal Entries",
  CreateJournalEntry: "Journal Entries",
  PayrollPayments: "Payroll Payments",
  PayrollPaymentUpload: "Payroll Payments",
  // FIN — POS Reports (Finance Essentials Reporting Pack). One permission
  // gates the hub and every drill-down for V1. Per-report permissions can
  // be split later if accounting needs role-based access.
  FinanceReportsHub: "General Report",
  SalesSummaryReport: "General Report",
  TenderSummaryReport: "General Report",
  TaxSummaryReport: "General Report",
  RefundSummaryReport: "General Report",
  VoidSummaryReport: "General Report",
  CashupReport: "General Report",
  TransactionDetailReport: "General Report",
  SalesByProductGroupReport: "General Report",
  SalesByProductReport: "General Report",
  CashupXZReport: "General Report",
};

interface ProtectedRouteProps {
  requiredModule: any;
  children: React.ReactNode;
}

export default function ProtectedRoute({
  requiredModule,
  children,
}: ProtectedRouteProps) {
  const { modules, isLoading } = useContext(ModulesContext);

  const hasPermission = (modules: any[], permissionName: string): boolean => {
    if (!permissionName || !Array.isArray(modules)) return false;
    return modules.some((section: any) => {
      const value = section?.value;
      if (!Array.isArray(value)) return false;
      return value.some(
        (item: any) =>
          (item.name === permissionName ||
            PERMISSION_ALIASES[permissionName]?.includes(item.name)) &&
          item.value === true
      );
    });
  };

  const getPermissionName = (component: any): string => {
    if (typeof component === "string") {
      return COMPONENT_TO_PERMISSION_MAP[component] || component;
    }
    const componentName = component?.name || component?.displayName || "";
    return COMPONENT_TO_PERMISSION_MAP[componentName] || componentName;
  };

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F7679] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading permissions...</p>
        </div>
      </div>
    );
  }

  if (modules.length === 0) {
    console.warn("⚠️ No modules found, redirecting to dashboard");
    const language = localStorage.getItem("language") || "en";
    return <Navigate to={`/${language}/dashboard`} replace />;
  }

  const permissionName = getPermissionName(requiredModule);
  const allowed = hasPermission(modules, permissionName);

  if (!allowed) {
    const language = localStorage.getItem("language") || "en";
    return <Navigate to={`/${language}/403`} replace />;
  }

  return <>{children}</>;
}
